import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronDown } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import { Avisos, descargarExcel, claseMonto } from '../components/contabilidad/ReporteFinanciero';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// Balance general SIMPLIFICADO — #110. Provisional hasta la partida doble
// (#114): el patrimonio cierra por diferencia, así que siempre cuadra. El
// cálculo vive en el backend (estadosFinancierosController).

export default function BalanceGeneral() {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  // ?fecha= (enlace desde el Cierre del período, #121).
  const [params] = useSearchParams();
  const [fecha, setFecha] = useState(() => (/^\d{4}-\d{2}-\d{2}$/.test(params.get('fecha') || '') ? params.get('fecha') : hoyCaracas()));
  const [vista, setVista] = useVistaMoneda();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [abiertos, setAbiertos] = useState({});

  useEffect(() => {
    if (!empresaActiva || !fecha) return;
    setCargando(true);
    setError('');
    api
      .get('/reportes/balance-general', { params: { fecha, vista } })
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el balance general'))
      .finally(() => setCargando(false));
  }, [empresaActiva, fecha, vista]);

  async function exportar() {
    await descargarExcel(
      '/reportes/balance-general/excel',
      { fecha, vista },
      `balance-general${empresaNombre ? `-${empresaNombre}` : ''}-${fecha}.xlsx`
    );
  }

  const alternar = (clave) => setAbiertos((prev) => ({ ...prev, [clave]: !prev[clave] }));

  function Parte({ titulo, bloques, total }) {
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-3">{titulo}</th>
              <th className="p-3 text-right">Monto</th>
            </tr>
          </thead>
          <tbody>
            {bloques.map((b) => {
              const clave = `${titulo}|${b.key}`;
              const abierto = !!abiertos[clave];
              const expandible = b.lineas.length > 0;
              return (
                <React.Fragment key={clave}>
                  <tr
                    {...(expandible ? filaClickeable(() => alternar(clave)) : {})}
                    className={`border-t hover:bg-slate-50 transition-colors ${expandible ? `cursor-pointer ${FOCO_FILA}` : ''}`}
                  >
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-[14px] print:hidden">
                          {expandible && (abierto ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />)}
                        </span>
                        {b.label}
                        {expandible && b.lineas.length > 1 && <span className="text-xs text-slate-400">({b.lineas.length})</span>}
                      </span>
                      {b.sin_cierre && <span className="block text-xs text-slate-400 ml-5">Sin cierre de inventario a esta fecha</span>}
                    </td>
                    <td className={`p-3 text-right whitespace-nowrap font-medium ${claseMonto(b.total)}`}>{formatMonto(b.total)}</td>
                  </tr>
                  {abierto && b.lineas.map((l, i) => (
                    <tr key={`${clave}|${i}`} className="border-t text-slate-500">
                      <td className="p-3" style={{ paddingLeft: 40 }}>
                        {l.nombre}
                        {l.moneda === 'BS' && (
                          <span className="text-xs text-slate-400"> · Bs {formatMonto(l.saldo_original)}</span>
                        )}
                        {l.documentos > 0 && <span className="text-xs text-slate-400"> · {l.documentos} doc.</span>}
                      </td>
                      <td className={`p-3 text-right whitespace-nowrap ${claseMonto(l.monto)}`}>{formatMonto(l.monto)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-800">
              <td className="p-3">Total {titulo.toLowerCase()}</td>
              <td className={`p-3 text-right whitespace-nowrap ${claseMonto(total)}`}>{formatMonto(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  const avisos = [];
  if (datos) {
    const a = datos.avisos;
    if (a.sin_tasa_corte) avisos.push({ texto: 'No hay tasa cargada para la fecha de corte: los saldos en Bs quedaron en 0.' });
    if (a.sin_cierre_inventario) {
      avisos.push({ texto: 'No hay cierre de inventario a esta fecha: el inventario sale en 0.', enlace: { to: '/inventario/cierre', label: 'Cargar un cierre' } });
    }
    if (a.productos_sin_costo) avisos.push({ texto: `${a.productos_sin_costo} productos del cierre de inventario no tienen costo y no suman al valor.` });
    if (a.movimientos_sin_clasificar || a.movimientos_sin_categoria) {
      avisos.push({
        texto: 'Hay movimientos sin clasificar o sin categoría: préstamos, activo fijo y socios solo salen de las categorías clasificadas.',
        enlace: { to: '/categorias', label: 'Clasificar en Cuentas Contables' }
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Balance general
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <SelectorMoneda vista={vista} onChange={setVista} />
          <Exportar onExcel={exportar} disabled={!datos} />
        </div>
      </div>

      <EncabezadoImpresion titulo="Balance general (simplificado)" detalle={`Al ${formatFecha(fecha)} · montos en ${vista === 'BCV' ? 'USD a tasa BCV' : 'USD'}`} />

      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Fecha de corte</label>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="border rounded-md px-3 py-2 text-sm" />
        </div>
        <p className="text-xs text-slate-400 max-w-md">
          Balance simplificado: el patrimonio cierra por diferencia. Se reemplaza por el formal cuando llegue la partida doble (#114).
        </p>
      </div>

      <Avisos avisos={avisos} />

      {error ? (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center text-red-600 text-sm">{error}</div>
      ) : cargando || !datos ? (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center text-slate-400 text-sm">Cargando…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <Parte titulo="Activo" bloques={datos.activo} total={datos.total_activo} />
          <div className="space-y-6">
            <Parte titulo="Pasivo" bloques={datos.pasivo} total={datos.total_pasivo} />
            <Parte titulo="Patrimonio" bloques={datos.patrimonio} total={datos.total_patrimonio} />
            <div className="bg-white rounded-xl shadow-sm flex items-center justify-between p-3 text-sm font-semibold text-slate-800">
              <span>Pasivo + patrimonio</span>
              <span className={claseMonto(datos.total_pasivo + datos.total_patrimonio)}>
                {formatMonto(datos.total_pasivo + datos.total_patrimonio)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
